import React, { useState, useEffect } from 'react';
import {
    Plus, Trash2, ChevronUp, ChevronDown, GripVertical,
    BookOpen, Brain, CheckSquare, FileText, Upload, PenTool,
    ChevronDown as ExpandIcon, ChevronRight, FormInput,
    Video, Minus, AlertTriangle, Info, Lightbulb, Heading
} from 'lucide-react';
import QuizBuilder from './QuizBuilder';
import TrainingBuilder from './TrainingBuilder';
import ChecklistBuilder from './ChecklistBuilder';
import FormFieldsBuilder from './FormFieldsBuilder';
import PlaceholderTextarea from '../Courses/PlaceholderTextarea';

// Section type definitions
const SECTION_TYPES = [
    { type: 'training', label: 'Training', icon: BookOpen, color: 'blue', description: 'Add training content with videos, PDFs, and text' },
    { type: 'quiz', label: 'Quiz', icon: Brain, color: 'green', description: 'Add quiz questions to test knowledge' },
    { type: 'checklist', label: 'Checklist', icon: CheckSquare, color: 'amber', description: 'Add a checklist of items to complete' },
    { type: 'form_fields', label: 'Form Fields', icon: FormInput, color: 'indigo', description: 'Add custom text inputs for employee responses' },
    { type: 'content', label: 'Content', icon: FileText, color: 'gray', description: 'Add simple text content or instructions' },
    { type: 'video', label: 'Video', icon: Video, color: 'red', description: 'Embed a YouTube, Vimeo, or direct video link' },
    { type: 'divider', label: 'Divider', icon: Minus, color: 'slate', description: 'Add visual spacing or a labeled separator' },
    { type: 'callout', label: 'Callout', icon: Info, color: 'cyan', description: 'Highlighted info, tip, or warning box' },
    { type: 'heading', label: 'Heading', icon: Heading, color: 'orange', description: 'Section heading to organize content' },
    { type: 'proof_upload', label: 'Proof Upload', icon: Upload, color: 'purple', description: 'Require file upload as proof of completion' },
    { type: 'signature', label: 'Signature', icon: PenTool, color: 'rose', description: 'Require digital signature acknowledgment' }
];

const CombinedTaskBuilder = ({ sections = [], onChange, onContentChange, focusedIndex }) => {
    const [expandedSections, setExpandedSections] = useState({});
    const [showAddMenu, setShowAddMenu] = useState(false);

    // Auto-expand sections when they are loaded for the first time
    useEffect(() => {
        if (sections && sections.length > 0 && Object.keys(expandedSections).length === 0) {
            const expanded = {};
            sections.forEach((_, idx) => { expanded[idx] = true; });
            setExpandedSections(expanded);
        }
    }, [sections?.length]);

    // Parse section content from string to object when loaded from DB
    // (task_sections.content is TEXT in DB, so it comes back as a JSON string)
    useEffect(() => {
        if (!sections || sections.length === 0) return;
        let needsUpdate = false;
        const parsed = sections.map(section => {
            if (typeof section.content === 'string' && (section.content.trim().startsWith('{') || section.content.trim().startsWith('['))) {
                try {
                    needsUpdate = true;
                    return { ...section, content: JSON.parse(section.content) };
                } catch (e) {
                    return section;
                }
            }
            return section;
        });
        if (needsUpdate) {
            onChange(parsed);
        }
    }, []);

    const handleSectionsChange = (updatedSections) => {
        onChange(updatedSections);
    };

    const addSection = (sectionType) => {
        const decorativeTypes = ['divider', 'heading', 'callout', 'video'];
        const newSection = {
            id: Date.now(),
            section_type: sectionType,
            title: `${SECTION_TYPES.find(t => t.type === sectionType)?.label || 'Section'} ${sections.length + 1}`,
            description: '',
            content: getDefaultContent(sectionType),
            is_required: !decorativeTypes.includes(sectionType),
            passing_score: sectionType === 'quiz' ? 70 : null,
            grading_type: sectionType === 'quiz' ? 'auto' : null
        };

        const updated = [...sections, newSection];
        handleSectionsChange(updated);
        setExpandedSections(prev => ({ ...prev, [updated.length - 1]: true }));
        setShowAddMenu(false);
        onContentChange?.('combined_sections', `Added ${SECTION_TYPES.find(t => t.type === sectionType)?.label || 'section'} section`);
    };

    const getDefaultContent = (sectionType) => {
        switch (sectionType) {
            case 'quiz':
                return { questions: [] };
            case 'training':
                return { sections: [] };
            case 'checklist':
                return { items: [] };
            case 'form_fields':
                return { fields: [], instructions: '' };
            case 'content':
                return '';
            case 'video':
                return { url: '', caption: '' };
            case 'divider':
                return { label: '', style: 'solid' };
            case 'callout':
                return { type: 'info', message: '' };
            case 'heading':
                return { text: '', level: 'h2', subtitle: '' };
            case 'proof_upload':
                return { instructions: '', allowed_types: ['image', 'pdf', 'document'] };
            case 'signature':
                return { acknowledgment_text: '' };
            default:
                return null;
        }
    };

    const updateSection = (index, field, value) => {
        const updated = [...sections];
        updated[index] = { ...updated[index], [field]: value };
        handleSectionsChange(updated);

        let valueDisplay = value;
        if (typeof value === 'string' && value.length > 50) {
            valueDisplay = value.substring(0, 50) + '...';
        }
        onContentChange?.('combined_sections', `Updated ${field} for section ${index + 1} to: "${valueDisplay}"`);
    };

    const updateSectionContent = (index, content) => {
        const updated = [...sections];
        updated[index] = { ...updated[index], content };
        handleSectionsChange(updated);
    };

    const deleteSection = (index) => {
        const sectionTitle = sections[index]?.title || `Section ${index + 1}`;
        const updated = sections.filter((_, i) => i !== index);
        handleSectionsChange(updated);
        onContentChange?.('combined_sections', `Deleted section: ${sectionTitle}`);
    };

    const moveSection = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < sections.length) {
            const updated = [...sections];
            [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
            handleSectionsChange(updated);
            // Update expanded state
            setExpandedSections(prev => ({
                ...prev,
                [index]: prev[newIndex],
                [newIndex]: prev[index]
            }));
        }
    };

    const toggleExpand = (index) => {
        setExpandedSections(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const getSectionTypeConfig = (type) => {
        return SECTION_TYPES.find(t => t.type === type) || SECTION_TYPES[0];
    };

    const renderSectionBuilder = (section, index) => {
        switch (section.section_type) {
            case 'quiz':
                return (
                    <QuizBuilder
                        content={section.content}
                        onChange={(content) => updateSectionContent(index, typeof content === 'string' ? JSON.parse(content) : content)}
                        gradingType={section.grading_type}
                        onContentChange={(field, desc) => onContentChange?.(field, `(Section ${index + 1}) ${desc}`)}
                    />
                );
            case 'training':
                return (
                    <TrainingBuilder
                        content={section.content}
                        onChange={(content) => updateSectionContent(index, content)}
                        onContentChange={(field, desc) => onContentChange?.(field, `(Section ${index + 1}) ${desc}`)}
                    />
                );
            case 'checklist':
                return (
                    <ChecklistBuilder
                        content={section.content}
                        onChange={(content) => updateSectionContent(index, typeof content === 'string' ? JSON.parse(content) : content)}
                        onContentChange={(field, desc) => onContentChange?.(field, `(Section ${index + 1}) ${desc}`)}
                    />
                );
            case 'form_fields':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Instructions (optional)</label>
                            <PlaceholderTextarea
                                value={section.content?.instructions || ''}
                                onChange={(e) => updateSectionContent(index, { ...section.content, instructions: e.target.value })}
                                placeholder="Provide instructions for filling out the form..."
                                rows={2}
                            />
                        </div>
                        <FormFieldsBuilder
                            fields={section.content?.fields || []}
                            onChange={(fields) => updateSectionContent(index, { ...section.content, fields })}
                            onContentChange={(field, desc) => onContentChange?.(field, `(Section ${index + 1}) ${desc}`)}
                        />
                    </div>
                );
            case 'content':
                return (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Content</label>
                        <PlaceholderTextarea
                            value={typeof section.content === 'string' ? section.content : ''}
                            onChange={(e) => updateSectionContent(index, e.target.value)}
                            placeholder="Enter content or instructions..."
                            rows={6}
                        />
                    </div>
                );
            case 'video':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Video URL</label>
                            <input
                                type="url"
                                value={section.content?.url || ''}
                                onChange={(e) => updateSectionContent(index, { ...section.content, url: e.target.value })}
                                placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                                Supports YouTube, Vimeo, Loom, or direct video file URLs (.mp4, .webm)
                            </p>
                        </div>
                        {section.content?.url && (
                            <div className="rounded-lg overflow-hidden bg-black/5 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
                                {(() => {
                                    const url = section.content.url;
                                    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                                    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
                                    const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
                                    if (youtubeMatch) {
                                        return <iframe src={`https://www.youtube.com/embed/${youtubeMatch[1]}`} className="w-full aspect-video" allowFullScreen title="Video preview" />;
                                    } else if (vimeoMatch) {
                                        return <iframe src={`https://player.vimeo.com/video/${vimeoMatch[1]}`} className="w-full aspect-video" allowFullScreen title="Video preview" />;
                                    } else if (loomMatch) {
                                        return <iframe src={`https://www.loom.com/embed/${loomMatch[1]}`} className="w-full aspect-video" allowFullScreen title="Video preview" />;
                                    } else {
                                        return <video src={url} controls className="w-full aspect-video" />;
                                    }
                                })()}
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Caption (optional)</label>
                            <input
                                type="text"
                                value={section.content?.caption || ''}
                                onChange={(e) => updateSectionContent(index, { ...section.content, caption: e.target.value })}
                                placeholder="Brief description of the video..."
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                    </div>
                );
            case 'divider':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Label (optional)</label>
                            <input
                                type="text"
                                value={section.content?.label || ''}
                                onChange={(e) => updateSectionContent(index, { ...section.content, label: e.target.value })}
                                placeholder="e.g. Next Steps, Important Info..."
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Style</label>
                            <div className="flex gap-3">
                                {['solid', 'dashed', 'dotted'].map(style => (
                                    <label key={style} className="inline-flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name={`divider-style-${index}`}
                                            checked={(section.content?.style || 'solid') === style}
                                            onChange={() => updateSectionContent(index, { ...section.content, style })}
                                            className="w-4 h-4 text-primary"
                                        />
                                        <span className="text-sm capitalize text-gray-700 dark:text-slate-200">{style}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        {/* Preview */}
                        <div className="pt-2">
                            <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">Preview:</p>
                            <div className="flex items-center gap-3">
                                <div className={`flex-1 border-t-2 border-${section.content?.style || 'solid'} border-gray-300 dark:border-slate-600`}
                                    style={{ borderStyle: section.content?.style || 'solid' }} />
                                {section.content?.label && (
                                    <>
                                        <span className="text-sm font-medium text-gray-500 dark:text-slate-400 whitespace-nowrap">{section.content.label}</span>
                                        <div className={`flex-1 border-t-2 border-gray-300 dark:border-slate-600`}
                                            style={{ borderStyle: section.content?.style || 'solid' }} />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            case 'callout':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Callout Type</label>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { value: 'info', label: 'Info', icon: Info, color: 'blue' },
                                    { value: 'tip', label: 'Tip', icon: Lightbulb, color: 'green' },
                                    { value: 'warning', label: 'Warning', icon: AlertTriangle, color: 'amber' },
                                    { value: 'important', label: 'Important', icon: AlertTriangle, color: 'red' }
                                ].map(opt => {
                                    const OptIcon = opt.icon;
                                    const isSelected = (section.content?.type || 'info') === opt.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => updateSectionContent(index, { ...section.content, type: opt.value })}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${isSelected
                                                ? `bg-${opt.color}-50 dark:bg-${opt.color}-500/10 border-${opt.color}-300 dark:border-${opt.color}-500/30 text-${opt.color}-700 dark:text-${opt.color}-400`
                                                : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                                                }`}
                                        >
                                            <OptIcon className="w-4 h-4" />
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Message</label>
                            <PlaceholderTextarea
                                value={section.content?.message || ''}
                                onChange={(e) => updateSectionContent(index, { ...section.content, message: e.target.value })}
                                placeholder="Enter your callout message..."
                                rows={4}
                            />
                        </div>
                        {/* Preview */}
                        {section.content?.message && (
                            <div className="pt-2">
                                <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">Preview:</p>
                                {(() => {
                                    const calloutType = section.content?.type || 'info';
                                    const styles = {
                                        info: { bg: 'bg-blue-50 dark:bg-blue-500/10', border: 'border-blue-200 dark:border-blue-500/30', text: 'text-blue-800 dark:text-blue-300', Icon: Info },
                                        tip: { bg: 'bg-green-50 dark:bg-green-500/10', border: 'border-green-200 dark:border-green-500/30', text: 'text-green-800 dark:text-green-300', Icon: Lightbulb },
                                        warning: { bg: 'bg-amber-50 dark:bg-amber-500/10', border: 'border-amber-200 dark:border-amber-500/30', text: 'text-amber-800 dark:text-amber-300', Icon: AlertTriangle },
                                        important: { bg: 'bg-red-50 dark:bg-red-500/10', border: 'border-red-200 dark:border-red-500/30', text: 'text-red-800 dark:text-red-300', Icon: AlertTriangle }
                                    };
                                    const s = styles[calloutType] || styles.info;
                                    const CalloutIcon = s.Icon;
                                    return (
                                        <div className={`flex items-start gap-3 p-4 rounded-lg border ${s.bg} ${s.border}`}>
                                            <CalloutIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${s.text}`} />
                                            <p className={`text-sm ${s.text}`}>{section.content.message}</p>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                );
            case 'heading':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Heading Text</label>
                            <input
                                type="text"
                                value={section.content?.text || ''}
                                onChange={(e) => updateSectionContent(index, { ...section.content, text: e.target.value })}
                                placeholder="Enter heading text..."
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Heading Size</label>
                            <div className="flex gap-3">
                                {[
                                    { value: 'h1', label: 'Large (H1)' },
                                    { value: 'h2', label: 'Medium (H2)' },
                                    { value: 'h3', label: 'Small (H3)' }
                                ].map(opt => (
                                    <label key={opt.value} className="inline-flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name={`heading-level-${index}`}
                                            checked={(section.content?.level || 'h2') === opt.value}
                                            onChange={() => updateSectionContent(index, { ...section.content, level: opt.value })}
                                            className="w-4 h-4 text-primary"
                                        />
                                        <span className="text-sm text-gray-700 dark:text-slate-200">{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Subtitle (optional)</label>
                            <input
                                type="text"
                                value={section.content?.subtitle || ''}
                                onChange={(e) => updateSectionContent(index, { ...section.content, subtitle: e.target.value })}
                                placeholder="Optional subtitle or description..."
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                        {/* Preview */}
                        {section.content?.text && (
                            <div className="pt-2">
                                <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">Preview:</p>
                                <div className="p-4 bg-gray-50 dark:bg-slate-800/50 rounded-lg">
                                    {section.content.level === 'h1' && <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{section.content.text}</h1>}
                                    {(section.content.level === 'h2' || !section.content.level) && <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{section.content.text}</h2>}
                                    {section.content.level === 'h3' && <h3 className="text-lg font-medium text-gray-900 dark:text-white">{section.content.text}</h3>}
                                    {section.content.subtitle && <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">{section.content.subtitle}</p>}
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 'proof_upload':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Upload Instructions</label>
                            <PlaceholderTextarea
                                value={section.content?.instructions || ''}
                                onChange={(e) => updateSectionContent(index, { ...section.content, instructions: e.target.value })}
                                placeholder="Describe what the employee should upload as proof..."
                                rows={3}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Allowed File Types</label>
                            <div className="flex flex-wrap gap-2">
                                {['image', 'pdf', 'document', 'video'].map(type => (
                                    <label key={type} className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-slate-800/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:bg-slate-800">
                                        <input
                                            type="checkbox"
                                            checked={section.content?.allowed_types?.includes(type) || false}
                                            onChange={(e) => {
                                                const types = section.content?.allowed_types || [];
                                                const updated = e.target.checked
                                                    ? [...types, type]
                                                    : types.filter(t => t !== type);
                                                updateSectionContent(index, { ...section.content, allowed_types: updated });
                                            }}
                                            className="w-4 h-4 text-primary rounded"
                                        />
                                        <span className="text-sm capitalize">{type}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'signature':
                return (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Acknowledgment Text</label>
                        <PlaceholderTextarea
                            value={section.content?.acknowledgment_text || ''}
                            onChange={(e) => updateSectionContent(index, { acknowledgment_text: e.target.value })}
                            placeholder="Enter the text the employee must acknowledge by signing..."
                            rows={4}
                        />
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                            The employee will see this text and must digitally sign to acknowledge.
                        </p>
                    </div>
                );
            default:
                return <p className="text-gray-500 dark:text-slate-400">Unknown section type</p>;
        }
    };

    // Focused mode: only render the builder content for a specific section
    if (focusedIndex !== undefined && focusedIndex !== null && sections[focusedIndex]) {
        return (
            <div className="focused-builder">
                {renderSectionBuilder(sections[focusedIndex], focusedIndex)}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Task Builder</h3>
                    <p className="text-sm text-gray-500 dark:text-slate-400">Add sections to create your task</p>
                </div>
            </div>

            {/* Section List */}
            {sections.length === 0 ? (
                <div className="bg-gray-50 dark:bg-slate-800/50 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center">
                    <p className="text-gray-600 dark:text-slate-300 mb-4">No sections yet. Add sections to build your task.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {sections.map((section, index) => {
                        const config = getSectionTypeConfig(section.section_type);
                        const Icon = config.icon;
                        const isExpanded = expandedSections[index];

                        return (
                            <div
                                key={section.id || index}
                                className={`border rounded-xl overflow-hidden transition-all ${isExpanded ? 'border-gray-300 dark:border-slate-600 shadow-sm' : 'border-gray-200 dark:border-slate-700'
                                    }`}
                            >
                                {/* Section Header */}
                                <div
                                    className={`flex items-center gap-3 p-4 cursor-pointer ${isExpanded ? 'bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200' : 'bg-white dark:bg-slate-900 hover:bg-gray-50 dark:bg-slate-800/50'
                                        }`}
                                    onClick={() => toggleExpand(index)}
                                >
                                    {/* Drag Handle & Reorder */}
                                    <div className="flex flex-col gap-0.5" onClick={e => e.stopPropagation()}>
                                        <button
                                            type="button"
                                            onClick={() => moveSection(index, 'up')}
                                            disabled={index === 0}
                                            className="p-0.5 text-gray-400 hover:text-gray-600 dark:text-slate-300 disabled:opacity-30"
                                        >
                                            <ChevronUp className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => moveSection(index, 'down')}
                                            disabled={index === sections.length - 1}
                                            className="p-0.5 text-gray-400 hover:text-gray-600 dark:text-slate-300 disabled:opacity-30"
                                        >
                                            <ChevronDown className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Section Type Badge */}
                                    <div className={`p-2 rounded-lg bg-${config.color}-100`}>
                                        <Icon className={`w-5 h-5 text-${config.color}-600`} />
                                    </div>

                                    {/* Section Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-gray-500 dark:text-slate-400">Section {index + 1}</span>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}>
                                                {config.label}
                                            </span>
                                            {section.is_required && (
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-500/20 text-red-700">
                                                    Required
                                                </span>
                                            )}
                                        </div>
                                        <input
                                            type="text"
                                            value={section.title}
                                            onChange={(e) => updateSection(index, 'title', e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="font-medium text-gray-900 dark:text-white bg-transparent border-none focus:ring-0 p-0 w-full"
                                            placeholder="Section title..."
                                        />
                                    </div>

                                    {/* Expand/Collapse & Delete */}
                                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                        <button
                                            type="button"
                                            onClick={() => deleteSection(index)}
                                            className="p-2 text-red-600 hover:bg-red-50 dark:bg-red-500/10 rounded-lg transition-colors"
                                            title="Delete Section"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => toggleExpand(index)}
                                            className="p-2 text-gray-400 hover:text-gray-600 dark:text-slate-300 rounded-lg transition-colors"
                                        >
                                            {isExpanded ? <ExpandIcon className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                {/* Section Content (Expanded) */}
                                {isExpanded && (
                                    <div className="p-4 bg-white dark:bg-slate-900">
                                        {/* Section Settings */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pb-4 border-b border-gray-100 dark:border-slate-700/50">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Description</label>
                                                <input
                                                    type="text"
                                                    value={section.description || ''}
                                                    onChange={(e) => updateSection(index, 'description', e.target.value)}
                                                    placeholder="Brief description of this section..."
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                                                />
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={section.is_required}
                                                        onChange={(e) => updateSection(index, 'is_required', e.target.checked)}
                                                        className="w-4 h-4 text-primary rounded"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-slate-200">Required</span>
                                                </label>
                                                {section.section_type === 'quiz' && (
                                                    <>
                                                        <div className="flex items-center gap-2">
                                                            <label className="text-sm text-gray-700 dark:text-slate-200">Grading:</label>
                                                            <select
                                                                value={section.grading_type || 'auto'}
                                                                onChange={(e) => updateSection(index, 'grading_type', e.target.value)}
                                                                className="px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-900 focus:outline-none focus:ring-1 focus:ring-primary"
                                                            >
                                                                <option value="auto">Auto-Graded</option>
                                                                <option value="manual">Manual Review</option>
                                                            </select>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <label className="text-sm text-gray-700 dark:text-slate-200">Pass Score:</label>
                                                            <input
                                                                type="number"
                                                                value={section.passing_score || 70}
                                                                onChange={(e) => updateSection(index, 'passing_score', parseInt(e.target.value))}
                                                                min="0"
                                                                max="100"
                                                                className="w-16 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm"
                                                            />
                                                            <span className="text-sm text-gray-500 dark:text-slate-400">%</span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Section-specific Builder */}
                                        {renderSectionBuilder(section, index)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add Section Button */}
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setShowAddMenu(!showAddMenu)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl text-gray-600 dark:text-slate-300 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all font-medium"
                >
                    <Plus className="w-5 h-5" />
                    Add Section
                </button>

                {/* Section Type Menu */}
                {showAddMenu && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl shadow-lg z-10 p-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                        {SECTION_TYPES.map(type => {
                            const Icon = type.icon;
                            return (
                                <button
                                    key={type.type}
                                    type="button"
                                    onClick={() => addSection(type.type)}
                                    className={`flex items-center gap-3 p-3 rounded-lg hover:bg-${type.color}-50 transition-colors text-left`}
                                >
                                    <div className={`p-2 rounded-lg bg-${type.color}-100`}>
                                        <Icon className={`w-5 h-5 text-${type.color}-600`} />
                                    </div>
                                    <div>
                                        <div className="font-medium text-gray-900 dark:text-white text-sm">{type.label}</div>
                                        <div className="text-xs text-gray-500 dark:text-slate-400">{type.description}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Summary */}
            {sections.length > 0 && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-primary">
                            Total Sections: {sections.length}
                        </span>
                        <span className="font-medium text-primary">
                            Required: {sections.filter(s => s.is_required).length}
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {sections.map((section, idx) => {
                            const config = getSectionTypeConfig(section.section_type);
                            return (
                                <span
                                    key={idx}
                                    className={`px-2 py-1 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}
                                >
                                    {idx + 1}. {config.label}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CombinedTaskBuilder;
